import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Mail, Lock, KeyRound, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useAuthTheme, AuthThemeSwitcher } from '../components/AuthThemeSwitcher';
import './ForgotPassword.css';

const STEP_EMAIL = 1;
const STEP_CODE = 2;
const STEP_PASSWORD = 3;
const STEP_SUCCESS = 4;

export default function ForgotPassword() {
  const [step, setStep] = useState(STEP_EMAIL);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { theme, toggleTheme } = useAuthTheme();

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setStep(STEP_CODE);
    } catch (err) {
      setError(err.message || 'Failed to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/verify-code', { email, code });
      setStep(STEP_PASSWORD);
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { email, code, newPassword });
      setStep(STEP_SUCCESS);
    } catch (err) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case STEP_EMAIL:
        return (
          <form onSubmit={handleSendCode}>
            <h2>Forgot Password?</h2>
            <p className="fp-subtitle">Enter your email to receive a verification code</p>
            <div className="fp-input-group">
              <Mail size={18} />
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            {error && <div className="fp-error">{error}</div>}
            <button type="submit" className="fp-btn-primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send Code'}
            </button>
          </form>
        );

      case STEP_CODE:
        return (
          <form onSubmit={handleVerifyCode}>
            <h2>Enter Verification Code</h2>
            <p className="fp-subtitle">We sent a 6-digit code to {email}</p>
            <div className="fp-input-group">
              <KeyRound size={18} />
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                required
                autoFocus
              />
            </div>
            {error && <div className="fp-error">{error}</div>}
            <button type="submit" className="fp-btn-primary" disabled={loading || code.length !== 6}>
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
            <button
              type="button"
              className="fp-btn-link"
              onClick={() => setStep(STEP_EMAIL)}
            >
              Use a different email
            </button>
          </form>
        );

      case STEP_PASSWORD:
        return (
          <form onSubmit={handleResetPassword}>
            <h2>Create New Password</h2>
            <p className="fp-subtitle">Enter your new password below</p>
            <div className="fp-input-group">
              <Lock size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoFocus
                minLength={6}
              />
              <button
                type="button"
                className="fp-eye-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="fp-input-group">
              <Lock size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            {error && <div className="fp-error">{error}</div>}
            <button type="submit" className="fp-btn-primary" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        );

      case STEP_SUCCESS:
        return (
          <div className="fp-success">
            <div className="fp-success-icon">&#10003;</div>
            <h2>Password Reset!</h2>
            <p className="fp-subtitle">Your password has been reset successfully.</p>
            <Link to="/login" className="fp-btn-primary">
              Go to Login
            </Link>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fp-container" data-auth-theme={theme}>
      <AuthThemeSwitcher theme={theme} toggleTheme={toggleTheme} />

      <div className="fp-card">
        <div className="fp-header">
          <Link to="/login" className="fp-back-link">
            <ArrowLeft size={18} /> Back to Login
          </Link>
        </div>
        {renderStep()}
      </div>
    </div>
  );
}
